export const removeTrailingSlash = (str: string) => str.replace(/\/$/, '');

/**
 * Concatenates a path segment to a URL in a way that handles trailing slashes.
 */
export const buildPath = ({ parent, path }: { parent: string; path: string }) =>
  removeTrailingSlash(parent) + path;

/**
 * Gets last piece of pathname after removing trailing slashes.
 */
export const getOnlyLastPath = (str: string): string =>
  str.substr(removeTrailingSlash(str).lastIndexOf('/') + 1);

/**
 * Removes everything after the last path segment, including query params.
 */
export const removeLastPath = (str: string) =>
  str.slice(0, removeTrailingSlash(str).lastIndexOf('/'));

/**
 * Gets the most extension-like piece of a file URI.
 */
export const getUriExtension = (str: string): string | null => {
  const split = str.split('.');

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return split.length > 1 ? split.pop() : null;
};

/**
 * Gets the root domain for given hostname.
 */
export const getRootDomain = (str: string): string => {
  // For production, strip the str to leave only 2 segments in the root domain
  // If using Crafting Sandbox, short-circuit it to use the whole string
  // See https://docs.sandboxes.cloud/docs/environment-variables
  if (import.meta.env.VITE_SANDBOX_ORG === 'persona') {
    return str;
  }

  const split = str.split('.');

  return split.slice(-2).join('.');
};

/**
 * Checks if the given URL is safe for the browser to redirect to in terms of
 * avoiding XSS, etc.
 *
 * We blocklist instead of allowlist which is fundamentally leaky, but building
 * a comprehensive allowlist isn't possible since some customers use custom
 * schemes for their mobile apps.
 */
export const isSafeRedirectUriScheme = (str: string | undefined): boolean => {
  if (str == null) return true;
  if (str === 'personacallback') return true;

  let scheme: string;
  try {
    scheme = new URL(str).protocol;
  } catch {
    // Invalid URL
    return false;
  }

  return !['data:', 'javascript:'].includes(scheme);
};

export const isAndroidIntent = (str: string): boolean => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return str && str.substring(0, 15) === 'intent:#Intent;' && str.substr(-4) === ';end';
};
